import { ComponentProps } from "@/props.ts";
import React, { useEffect, useState } from "react";
import { useNeurotuneStore } from "@/common/store/neurotune";
import { IProtocol, TProtocolId } from "../../types/protocol";
import { loadProtocols } from "../../services/protocols";
import { useNavigate } from "react-router";
import { Box, Text, Table, ActionIcon, Badge } from "@mantine/core";
import {IconFilePencil} from "@tabler/icons-react";
import dayjs from "dayjs";
import classes from "./ProtocolListPage.module.css";

interface ProtocolListPageProps extends ComponentProps {}

interface ProtocolWithSubject extends IProtocol {
  subjectName: string;
}

const ProtocolListPage: React.FC<
  ProtocolListPageProps
> = ({}: ProtocolListPageProps) => {
  const navigate = useNavigate();
  const activeCompany = useNeurotuneStore((state) => state.activeCompany);
  const subjects = useNeurotuneStore((state) => state.subjects);
  const loadSubjects = useNeurotuneStore((state) => state.loadSubjects);

  const [protocols, setProtocols] = useState<Array<ProtocolWithSubject>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load subjects if not already loaded
        if (subjects.length === 0) {
          await loadSubjects(activeCompany.id);
        }

        // Load protocols
        const loadedProtocols = await loadProtocols(activeCompany.id, {});

        // Map subject names to protocols
        const protocolsWithSubject = loadedProtocols.map((protocol) => {
          const subject = subjects.find((s) => s.id === protocol.subjectId);
          return {
            ...protocol,
            subjectName: subject?.name || protocol.subjectId,
          };
        });

        // Sort by createdAt descending (most recent first)
        protocolsWithSubject.sort(
          (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
        );

        setProtocols(protocolsWithSubject);
      } catch (error) {
        console.error("Failed to load protocols:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeCompany.id, subjects.length, loadSubjects]);

  const handleReviewClick = (protocolId: TProtocolId) => {
    navigate(`/sessions/review/${protocolId}`);
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case "in_progress":
        return (
          <Badge color="blue" variant="light">
            In Progress
          </Badge>
        );
      case "completed":
        return (
          <Badge color="green" variant="light">
            Completed
          </Badge>
        );
      default:
        return <Badge variant="light">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Text>Loading protocols...</Text>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.tableContainer}>
        <Table className={classes.table} striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Session Name</Table.Th>
              <Table.Th>Start Time</Table.Th>
              <Table.Th>Subject</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th style={{ width: "100px" }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {protocols.map((protocol) => (
              <Table.Tr key={protocol.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {protocol.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {dayjs(protocol.createdAt).format("MMM D, YYYY h:mm A")}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{protocol.subjectName}</Text>
                </Table.Td>
                <Table.Td>{formatStatus(protocol.status)}</Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="lg"
                    onClick={() => handleReviewClick(protocol.id)}
                    title="Review/Edit Session"
                  >
                    <IconFilePencil size={18} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
            {protocols.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="sm" c="dimmed" ta="center">
                    No protocols found
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export { ProtocolListPage };
